import { apiClient } from './apiClient';

export interface Feedback {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  status?: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  isPublished?: boolean;
  currentVersion?: number;
  publishedVersions?: number[];
}

export interface QuestionChoice {
  id?: string;
  position: number;
  value: string;
  text: string;
}

export interface Question {
  id?: string;
  name: string;
  title: string;
  helpText?: string;
  type: string;
  required: boolean;
  position: number;
  config?: any;
  choices: QuestionChoice[];
}

export interface Panel {
  id?: string;
  name: string;
  title?: string;
  description?: string;
  position: number;
  questions: Question[];
}

export interface Page {
  id?: string;
  position: number;
  title?: string;
  description?: string;
  questions: Question[];
  panels: Panel[];
}

export interface FeedbackStructure {
  feedbackId?: string;
  versionId?: string;
  versionNo?: number;
  versionTitle?: string;
  versionDescription?: string;
  publishedAt?: string;
  pages: Page[];
}

export interface CreateFeedbackDto {
  title: string;
  status?: string;
  createdBy?: string;
}

export interface UpdateFeedbackDto {
  title: string;
  status?: string;
}

export interface SubmitResponseDto {
  feedbackInstanceId?: string;
  feedbackOccurrenceId?: string;
  employeeId?: number;
  answers: Record<string, any>;
  meta?: any;
}

export interface ResponseDto {
  id: string;
  feedbackId: string;
  instanceId: string;
  employeeId: string;
  responseData: Record<string, any>;
  submittedAt: string;
}

export interface FeedbackVersion {
  versionNo: number;
  createdAt: string;
  publishedAt?: string;
}

class FeedbackService {
  async getFeedbacks(): Promise<Feedback[]> {
    const response = await apiClient.get('/api/feedback');
    return response.data;
  }

  async getFeedbackById(id: string): Promise<Feedback> {
    const response = await apiClient.get(`/api/feedback/${id}`);
    return response.data;
  }

  async createFeedback(dto: CreateFeedbackDto): Promise<Feedback> {
    const response = await apiClient.post('/api/feedback', dto);
    return response.data;
  }

  async updateFeedback(id: string, dto: UpdateFeedbackDto): Promise<Feedback> {
    const response = await apiClient.put(`/api/feedback/${id}`, dto);
    return response.data;
  }

  async deleteFeedback(id: string): Promise<void> {
    await apiClient.delete(`/api/feedback/${id}`);
  }

  async getFeedbackJs(id: string): Promise<FeedbackStructure> {
    const response = await apiClient.get(`/api/feedback/${id}/surveyjs`);
    return response.data;
  }

  async getFeedbackStructure(id: string, versionNo: number): Promise<FeedbackStructure> {
    const response = await apiClient.get(`/api/feedback/${id}/versions/${versionNo}/structure`);
    return response.data;
  }

  async updateFeedbackStructure(id: string, versionNo: number, structure: FeedbackStructure): Promise<FeedbackStructure> {
    const response = await apiClient.put(`/api/feedback/${id}/versions/${versionNo}/structure`, structure);
    return response.data;
  }

  async submitResponse(feedbackId: string, dto: SubmitResponseDto): Promise<ResponseDto> {
    const response = await apiClient.post(`/api/feedback/${feedbackId}/responses`, dto);
    return response.data;
  }

  async getFeedbackResponses(feedbackId: string): Promise<ResponseDto[]> {
    const response = await apiClient.get(`/api/feedback/${feedbackId}/responses`);
    return response.data;
  }

  async publishVersion(id: string, versionNo: number): Promise<void> {
    await apiClient.put(`/api/feedback/${id}/versions/${versionNo}/publish`);
  }

  async unpublishVersion(id: string, versionNo: number): Promise<void> {
    await apiClient.put(`/api/feedback/${id}/versions/${versionNo}/unpublish`);
  }

  async getFeedbackVersions(id: string): Promise<FeedbackVersion[]> {
    const response = await apiClient.get(`/api/feedback/${id}/versions`);
    return response.data;
  }

  async getMyResponseForInstance(instanceId: string): Promise<ResponseWithAnswersDto | null> {
    try {
      const response = await apiClient.get(`/api/responses/my-response/${instanceId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }
}

export interface AnswerDto {
  id: string;
  questionId: string;
  questionName: string;
  questionTitle: string;
  questionType: string;
  value: any;
  displayValue?: string;
  isFlagged?: boolean;
  flagLabel?: string;
  extra?: any;
}

export interface RespondentDto {
  id: number;
  email: string;
  fullName: string;
}

export interface ResponseWithAnswersDto {
  id: string;
  feedbackId: string;
  feedbackVersionId: string;
  versionNo: number;
  employeeId?: number;
  respondent?: RespondentDto;
  startedAt: string;
  submittedAt?: string;
  meta?: any;
  answers: AnswerDto[];
}

export const feedbackService = new FeedbackService();
